import { Trans, useLingui } from '@lingui/react/macro';
import FilterListIcon from '@mui/icons-material/FilterList';
import { Checkbox, Divider, dividerClasses, IconButton, Menu, MenuItem, Stack } from '@mui/material';
import {
  type GridColDef,
  GridColumnHeaderTitle,
  type GridFilterItem,
  type GridFilterOperator,
  gridFilterModelSelector,
  gridRowCountSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-pro';
import { type MouseEvent, useCallback, useId, useState } from 'react';

type MarketRow = { marketId: number; assetCurrency: string };
type MarketOption = { marketId: number; label: string };

const OPERATOR = 'market-any-of';
const NO_MARKETS: MarketOption[] = [];

const marketIsAnyOf = <R extends MarketRow>(): GridFilterOperator<R> => ({
  value: OPERATOR,
  getApplyFilterFn: (item: GridFilterItem) => {
    const selected = item.value as MarketOption[] | undefined;
    if (!selected?.length) return null;
    const ids = new Set(selected.map((market) => market.marketId));
    return (_value, row) => ids.has(row.marketId);
  },
});

// Compact rows on the 8px grid; MUI puts the divider's 8px margins on the item before it.
const MENU_ITEM_SX = {
  typography: 'body2',
  minHeight: 0,
  py: 0.5,
  px: 1,
  gap: 1,
  [`& + .${dividerClasses.root}`]: { my: 0.5 },
} as const;
const MENU_SLOT_PROPS = {
  paper: { variant: 'elevation', sx: { mt: 0.5 } },
  list: { sx: { py: 0.5 } },
} as const;

function MarketFilterHeader({
  field,
  headerName,
  columnWidth,
}: {
  readonly field: string;
  readonly headerName: string;
  readonly columnWidth: number;
}) {
  const { t } = useLingui();
  const apiRef = useGridApiContext();
  const rowCount = useGridSelector(apiRef, gridRowCountSelector);
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [options, setOptions] = useState<MarketOption[]>(NO_MARKETS);
  const menuId = useId();

  const item = filterModel.items.find((filterItem) => filterItem.id === field);
  const selected = (item?.value as MarketOption[] | undefined) ?? NO_MARKETS;
  const isActive = selected.length > 0;

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    const labels = new Map<number, string>();
    for (const row of apiRef.current.getRowModels().values()) labels.set(row.marketId, row.assetCurrency);
    // A checked market whose rows have all gone stays listed, so it can be unchecked.
    for (const market of selected) if (!labels.has(market.marketId)) labels.set(market.marketId, market.label);
    setOptions([...labels].map(([marketId, label]) => ({ marketId, label })).sort((a, b) => a.label.localeCompare(b.label)));
    setAnchorEl(event.currentTarget);
  };

  const setSelected = (next: MarketOption[]) => {
    if (next.length > 0) apiRef.current.upsertFilterItem({ id: field, field, operator: OPERATOR, value: next });
    else apiRef.current.deleteFilterItem({ id: field, field, operator: OPERATOR });
  };

  const toggle = (option: MarketOption) => {
    const isSelected = selected.some((market) => market.marketId === option.marketId);
    setSelected(isSelected ? selected.filter((market) => market.marketId !== option.marketId) : [...selected, option]);
  };

  // The grid's own title component, so the theme's columnHeaderTitle styles apply.
  const title = <GridColumnHeaderTitle label={headerName} columnWidth={columnWidth} />;
  // Nothing to filter, and no filter to clear. An open menu keeps its button, or it reopens on a detached anchor.
  if (rowCount === 0 && !isActive && !anchorEl) return title;

  return (
    <Stack direction="row" alignItems="center" gap={0.5} minWidth={0}>
      {title}
      <IconButton
        size="small"
        // The funnel glyph is top-heavy; in a centred row, 2px of top margin lowers it 1px to sit optically level.
        sx={{ p: 0.25, mt: 0.25 }}
        color={isActive ? 'primary' : 'default'}
        aria-label={t({ message: 'Filter by coin', comment: 'Table header button: opens the coin filter checklist' })}
        aria-haspopup="true"
        aria-controls={anchorEl ? menuId : undefined}
        aria-expanded={anchorEl ? 'true' : undefined}
        // Like MUI's own header buttons: tabIndex 0 would take the header's focus and dead-end its arrow keys,
        // and Enter would also reach the header, which sorts on it.
        tabIndex={-1}
        onKeyDown={(event) => event.stopPropagation()}
        onClick={handleOpen}
      >
        <FilterListIcon fontSize="small" />
      </IconButton>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={anchorEl != null}
        onClose={() => setAnchorEl(null)}
        // Drop below the button rather than over it.
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={MENU_SLOT_PROPS}
      >
        {options.map((option) => (
          <MenuItem key={option.marketId} sx={MENU_ITEM_SX} onClick={() => toggle(option)}>
            <Checkbox
              size="small"
              disableRipple
              tabIndex={-1}
              sx={{ p: 0 }}
              checked={selected.some((market) => market.marketId === option.marketId)}
            />
            {option.label}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem sx={MENU_ITEM_SX} disabled={!isActive} onClick={() => setSelected(NO_MARKETS)}>
          <Trans comment="Coin filter checklist: unchecks every coin">Clear</Trans>
        </MenuItem>
      </Menu>
    </Stack>
  );
}

/** Adds a coin checklist to a Coin column header, applied through the grid's filter model (keyed by `marketId`). */
export const withMarketFilter = <R extends MarketRow>(column: GridColDef<R>): GridColDef<R> => ({
  ...column,
  filterOperators: [marketIsAnyOf<R>()],
  renderHeader: (params) => (
    <MarketFilterHeader field={column.field} headerName={column.headerName ?? ''} columnWidth={params.colDef.computedWidth} />
  ),
});

/** Footer row count while the filter hides rows ("3 of 7"); MUI's own text is untranslated. */
export function useVisibleRowsLocaleText() {
  const { t } = useLingui();
  return useCallback(
    (visibleCount: number, totalCount: number) => {
      const visible = visibleCount.toLocaleString();
      const total = totalCount.toLocaleString();
      return t({
        message: `${visible} of ${total}`,
        comment: 'Table footer row count while a filter hides rows, e.g. "3 of 7"',
      });
    },
    [t]
  );
}
